import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {Button, Dialog, ErrorBlock, NavBar, Skeleton, Toast} from 'antd-mobile';
import {useQuery} from '@apollo/client';

import {
    GET_DSP_PACKAGE_DETAIL,
    GetDspPackageDetailByTrackingNumberResponse,
    GetDspPackageDetailByTrackingNumberVariables
} from "../../graphql/queries/getPackageDetail";
import {formatTimestampToSecondsTime} from "../../../../utils/dateTimeUtils";
import photoService, {PhotoType, PositionType} from 'pages/delivery/services/PhotoService';
import {useLocationStore} from 'store/locationStore';
import PhotoUploadCard from 'pages/delivery/components/PhotoUploadCard';
import MultiPhotoGalleryView from 'pages/delivery/components/MultiPhotoGalleryView';
import PhotoViewer from 'pages/delivery/components/PhotoViewer';
import {getBottomNavBarContainerStyle, getNavBarContainerStyle} from 'utils/deviceUtils';
import {NativePermissions} from 'pages/delivery/components/PhotoNativeBridge';
import {isAppVersionGreaterThanOrEqual, kNativeMultiImagePickerVersion} from 'utils/versionUtils';
import multiPhotoService from "../../../delivery/services/MultiPhotoService";
import {openCamera} from 'utils/appRuntime';
import {uploadPhotosWithTimeout} from 'services/DeliveryDataService';
import MultiPhotoSelectorView from "../../../delivery/components/MultiPhotoSelectorView";
import {InfoRow} from '../../../../components/InfoRow';
import {
    DeliveredEventInput,
    handleDeliveredEvent,
    handleDspDeliveredEvent
} from '../../../delivery/graphql/mutations/delivered';
import {t} from 'i18n';
import {useTranslation} from 'react-i18next';
import OrderNoteItem from '../../../../components/OrderNoteItem/OrderNoteItem';
import {DspPath} from "../../../../enums/DeliveryNavigatePathEnum";
import {formatStopNumber} from "../../../../utils/stopNumberUtils";
import {DspValidationType} from "../../../../enums/DspValidationType";
import {maskPhoneNumber} from "../../../../utils/phoneUtils";
import {
    GET_DELIVERY_METHOD_I18N_CONFIG,
    GetDeliveryMethodI18nConfigResponse
} from 'graphql/queries/getDeliveryMethodI18nConfig';
import DeliveryMethodSelector from 'pages/delivery/components/DeliveryMethodSelector';
import {getLastDeliveryMethod, setLastDeliveryMethod} from 'pages/delivery/utils/deliveryMethodStorage';
import DeliveryMethodDisplayCard from 'pages/delivery/components/DeliveryMethodDisplayCard';

const kMinSelectorPhotoLength = 3
const kMaxSelectorPhotoLength = 6

interface DspDeliveredPodProps {}

const DspDeliveredPodPage: React.FC<DspDeliveredPodProps> = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [photos, setPhotos] = useState<Record<PositionType, PhotoType | undefined>>({} as Record<PositionType, PhotoType | undefined>);
    const [photosArray, setPhotosArray] = useState<PhotoType[]>([]);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showMultiPhotoSelector, setShowMultiPhotoSelector] = useState<boolean>(false);
    const { position } = useLocationStore();
    const {trackingNumber} = useParams<{ trackingNumber: string }>();
    const urlTitle = searchParams.get('title');
    const urlValidationType = searchParams.get('validationType') as DspValidationType;
    const { i18n } = useTranslation();

    // 查询DSP包裹详情
    const {
        data: packageData,
        loading,
        error
    } = useQuery<GetDspPackageDetailByTrackingNumberResponse, GetDspPackageDetailByTrackingNumberVariables>(
        GET_DSP_PACKAGE_DETAIL,
        {
            variables: {
                trackingNumber: trackingNumber || ""
            },
            skip: !trackingNumber,
            fetchPolicy: 'cache-and-network'
        }
    );
    const packageDetail = packageData?.getDspPackageDetailByTrackingNumber;

    // 妥投方式配置与选中值
    const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<string | null>(null);
    const { data: deliveryMethodI18nData } = useQuery<GetDeliveryMethodI18nConfigResponse>(
        GET_DELIVERY_METHOD_I18N_CONFIG,
        { fetchPolicy: 'cache-first' }
    );
    const deliveryMethodI18nConfig = deliveryMethodI18nData?.getDeliveryMethodI18nConfig ?? [];
    // 默认选中：上次完成妥投时的选择 > 第一种妥投方式（仅未妥投时）
    useEffect(() => {
        if (deliveryMethodI18nConfig.length > 0 && selectedDeliveryMethod === null && !packageDetail?.isDelivered) {
            const stored = getLastDeliveryMethod();
            const code = stored && deliveryMethodI18nConfig.some(o => o.code === stored)
                ? stored
                : deliveryMethodI18nConfig[0].code;
            setSelectedDeliveryMethod(code);
        }
    }, [deliveryMethodI18nConfig, selectedDeliveryMethod, packageDetail?.isDelivered]);


    useEffect(() => {
        if (packageDetail) {
            const podImages = packageDetail?.podImageS3UrlsJson || [];
            const podImageKeys = packageDetail?.podImageS3KeysJson || [];
            
            if (showMultiPhotoSelector) {
                // 新版本：数组形式
                if (podImages.length > 0 && podImageKeys.length > 0) {
                    setPhotosArray(podImages.map((url, index) => ({
                        url,
                        objectKey: podImageKeys[index] || ''
                    })));
                }
            } else {
                // 老版本：固定位置
                if (podImages.length > 0 && podImageKeys.length > 0) {
                    setPhotos({
                        label: {
                            url: podImages[0],
                            objectKey: podImageKeys[0]
                        },
                        location: {
                            url: podImages[1],
                            objectKey: podImageKeys[1]
                        },
                        houseNumber: {
                            url: podImages[2],
                            objectKey: podImageKeys[2]
                        },
                    });
                }
            }
        }
    }, [packageDetail, showMultiPhotoSelector]);

    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await NativePermissions.request('camera');
                await NativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);

    // 版本检查
    useEffect(() => {
        const checkVersion = async () => {
             const isHigher = await isAppVersionGreaterThanOrEqual(kNativeMultiImagePickerVersion);
             setShowMultiPhotoSelector(isHigher);
        };

        checkVersion();
    }, []);

    if (loading) {
        return (
            <div className="flex flex-col h-screen bg-white">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>{trackingNumber}</NavBar>
                </div>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={5} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={3} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={1} animated/>
                <Skeleton style={{width: '100%', height: '30%'}} animated/>
            </div>
        );
    }

    if (!trackingNumber || error || !packageDetail) {
        return (
            <div className="p-4">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>{trackingNumber}</NavBar>
                </div>
                <ErrorBlock
                    status="empty"
                    title={t('pages.dsp.packageDetail.packageNotFound', '包裹不存在')}
                    description={t('pages.dsp.packageDetail.packageNotFoundDescription', '未找到该运单号对应的包裹信息')}
                />
            </div>
        );
    }

    const buildNavigationParams = () => {
        const queryParams = new URLSearchParams();
        if (urlTitle) {
            queryParams.set('title', urlTitle);
        }
        if (urlValidationType) {
            queryParams.set('validationType', urlValidationType);
        }
        return queryParams;
    };



    /*照片相关操作 start======*/

    // 拍照或从相册选择 - 老版本
    const takePhoto = async (type: PositionType, file: File) => {
        try {
            setIsLoading(true);
            await photoService.handlePhotoUnUpload(type, file, (updateFn) => {
                setPhotos((prev) => updateFn(prev));
            });
        } catch (error) {
            console.error('Failed to take photo:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 删除照片 - 老版本
    const deletePhoto = (type: PositionType) => {
        Dialog.confirm({
            content: t('pages.delivery.detail.deletePhotoTitle'),
            confirmText: t('pages.delivery.detail.deletePhotoDelete'),
            cancelText: t('pages.delivery.detail.deletePhotoCancel'),
            onConfirm: () => {
                photoService.handleDeletePhoto(type, (updateFn) => {
                    setPhotos((prev) => updateFn(prev));
                });
            },
        });
    };

    // 删除照片 - 新版本（数组）
    const deletePhotoArray = (index: number) => {
        Dialog.confirm({
            content: t('pages.delivery.detail.deletePhotoTitle'),
            confirmText: t('pages.delivery.detail.deletePhotoDelete'),
            cancelText: t('pages.delivery.detail.deletePhotoCancel'),
            onConfirm: () => {
                multiPhotoService.handleDeletePhotoArray(index, (updateFn) => {
                    setPhotosArray((prev) => updateFn(prev));
                });
            },
        });
    };

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    /*照片相关操作 end======*/

    // 处理多图画廊的连拍功能（新版本数组形式）
    const handleMultiPhotoGalleryBurstCapture = () => {
        // 计算剩余可拍照片数量
        const currentPhotoCount = photosArray.length;
        const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);
        
        // 动态计算最小选择数量：如果当前照片数已经超过最小要求，则最小选择为0
        const minSelectionCount = currentPhotoCount >= kMinSelectorPhotoLength ? 0 : kMinSelectorPhotoLength - currentPhotoCount;
        
        openCamera(remainingPhotoCount, minSelectionCount, t('pages.delivery.detail.requiredPhotos'), async (photos: string[]) => {
            try {
                await multiPhotoService.handleMultiPhotoSelectionArray(
                    photos,
                    (updateFn) => setPhotosArray(updateFn)
                );
            } catch (error) {
                console.error('多图片处理失败:', error);
                Toast.show({ content: t('pages.dsp.validation.multiPhotoProcessingFailed', '多图片处理失败'), position: 'center' });
            }
        });
    };

    // 完成派送
    const handleCompleteDelivery = async (): Promise<void> => {
        setIsLoading(true);

        try {
            // 验证照片
            if (showMultiPhotoSelector) {
                // 新版本：至少需要3张图片，有url即可
                if (photosArray.length < kMinSelectorPhotoLength || !photosArray.every(photo => photo.url)) {
                    Toast.show({
                        content: t('pages.delivery.detail.takeAtLeastPhotos', { count: kMinSelectorPhotoLength }),
                        position: 'center'
                    });
                    return;
                }
            } else {
                // 老版本：需要3张固定位置的图片
                const hasLabel = photos.label?.objectKey;
                const hasLocation = photos.location?.objectKey;
                const hasHouseNumber = photos.houseNumber?.objectKey;
                if (!hasLabel || !hasLocation || !hasHouseNumber) {
                    Toast.show({
                        content: t('pages.delivery.detail.takePhotosOfRequired'),
                        position: 'center'
                    });
                    return;
                }
            }

            // 获取需要上传的照片
            const photosToUpload = showMultiPhotoSelector
                ? photosArray.filter((item): item is PhotoType => item?.objectKey !== undefined && item?.url !== undefined)
                : Object.values(photos).filter((item): item is PhotoType => item?.objectKey !== undefined && item?.url !== undefined);

            // 执行照片上传
            const photoKeys = await uploadPhotosWithTimeout(photosToUpload, trackingNumber);

            // 直接调用 handleDeliveredEvent 方法
            const deliveredInput: DeliveredEventInput = {
                trackingNumber: trackingNumber,
                location: {
                    latitude: position?.latitude || 0.0,
                    longitude: position?.longitude || 0.0
                },
                deliveredAt: new Date().toISOString(),
                podImageS3KeysJson: photoKeys,
                id: packageDetail?.deliveryTaskOrder?.id || 0,
                deliveryMethod: selectedDeliveryMethod ?? undefined
            };
            
            await handleDspDeliveredEvent(deliveredInput);
            // 完成妥投时持久化本次选择的妥投方式，供下次自动勾选
            if (selectedDeliveryMethod) {
                setLastDeliveryMethod(selectedDeliveryMethod);
            }
            if (packageDetail?.isDelivered){
                Toast.show({ content: t('pages.dsp.home.modificationSuccessful'), position: 'center' });
            }else {
                Toast.show({ content: t('pages.dsp.home.deliveryCompleted'), position: 'center' });
            }

            if (urlTitle || urlValidationType) {
                const params = buildNavigationParams();
                navigate(`${DspPath.DELIVERY_DSP_PACKAGE_SCAN}?${params.toString()}`);
            } else {
                navigate(DspPath.DELIVERY_DSP_HOME);
            }
        } catch (error: any) {
            console.error('Failed to complete delivery:', error);
            Toast.show({content: t('pages.delivery.detail.deliveryFailed'), position: 'center'});
        } finally {
            setIsLoading(false);
        }
    };

    // 图片多选 
    const multiPhotoSelectorView = (() => {
        // 计算剩余可拍照片数量
        const currentPhotoCount = photosArray.length;
        const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);
        
        // 动态计算最小选择数量：如果当前照片数已经超过最小要求，则最小选择为0
        const minSelectionCount = currentPhotoCount >= kMinSelectorPhotoLength ? 0 : kMinSelectorPhotoLength - currentPhotoCount;
        
        return (
            <MultiPhotoSelectorView 
                minPhotos={minSelectionCount} 
                maxPhotos={remainingPhotoCount} 
                onPhotoSelected={async (photos: string[]) => {
                    try {
                        const result = await multiPhotoService.handleMultiPhotoSelectionArray(
                            photos,
                            (updateFn) => setPhotosArray(updateFn)
                        );
                        
                        if (!result.success) {
                            console.warn('部分图片上传失败:', result.errors);
                            // 可以显示提示信息
                        }
                    } catch (error) {
                        console.error('多图片处理失败:', error);
                    }
                }}
            />
        );
    })()

    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => {
                    if (urlTitle || urlValidationType) {
                        const params = buildNavigationParams();
                        navigate(`${DspPath.DELIVERY_DSP_PACKAGE_SCAN}?${params.toString()}`);
                    } else {
                        navigate(DspPath.DELIVERY_DSP_HOME);
                    }
                }}>
                    {trackingNumber}
                </NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="mb-1 bg-white rounded-lg p-4 shadow-sm">
                    <div className="mb-1">
                        <h2 className="text-base font-medium">{t('pages.dsp.packageDetail.orderInfo')}</h2>
                        <InfoRow
                            label={t('pages.dsp.packageDetail.trackingNo')}
                            value={packageDetail.trackingNumber}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.stop')}
                            value={packageDetail?.deliveryTaskOrder?.stopNumber != null ? formatStopNumber(packageDetail?.deliveryTaskOrder?.stopNumber,packageDetail?.deliveryTaskOrder?.subStopNumber) : '-'}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.route')}
                            value={packageDetail?.deliveryTaskOrder?.routeName || '-'}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.assignedTime')}
                            value={packageDetail?.deliverAt ? formatTimestampToSecondsTime(packageDetail?.deliverAt) : '-'}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.deliveryAttempt')}
                            value={`${packageDetail?.deliveryAttempts || 0} out of 3`}
                        />
                        <OrderNoteItem 
                            orderNote={packageDetail?.deliveryTaskOrder?.orderNote}
                            currentLanguage={i18n.language}
                        />
                        {/* 根据isDelivered状态显示不同的信息 */}
                        {packageDetail?.isDelivered && (
                            <>
                                <InfoRow
                                    label={t('pages.dsp.packageDetail.deliveredAt')}
                                    value={packageDetail?.deliveredAt ? formatTimestampToSecondsTime(packageDetail?.deliveredAt) : '-'}
                                />
                                <InfoRow
                                    label={t('pages.dsp.packageDetail.deliveredBy')}
                                    value={packageDetail?.deliveredBy || '-'}
                                />
                            </>
                        )}
                        <h2 className="text-base font-medium">{t('pages.dsp.packageDetail.customerInfo')}</h2>
                        <div>
                            <InfoRow
                                label={t('pages.dsp.packageDetail.customerName')}
                                value={packageDetail?.recipientName || '-'}
                            />
                            <InfoRow
                                label={t('pages.dsp.packageDetail.phoneNumber')}
                                value={maskPhoneNumber(packageDetail?.recipientPhone)}
                            />
                            <InfoRow
                                label={t('pages.dsp.packageDetail.address')}
                                value={packageDetail?.recipientAddress || '-'}
                            />
                            {packageDetail?.isDelivered && deliveryMethodI18nConfig.length > 0 && packageDetail?.deliveryMethod && (() => {
                                const item = deliveryMethodI18nConfig.find(i => i.code === packageDetail?.deliveryMethod);
                                if (!item) return null;
                                return <DeliveryMethodDisplayCard item={item} />;
                            })()}
                        </div>
                    </div>
                </div>

                {/* 妥投方式：
                    - 未妥投：显示下拉选择
                    - 已妥投：回显已保存的妥投方式 */}
                {!packageDetail?.isDelivered && deliveryMethodI18nConfig.length > 0 && (
                    <DeliveryMethodSelector
                        options={deliveryMethodI18nConfig}
                        value={selectedDeliveryMethod}
                        onChange={setSelectedDeliveryMethod}
                    />
                )}

                {/* 照片 - 根据状态显示不同的图片 */}
                {(() => {
                    return (
                        <div className="bg-white p-1">
                            <div className="mb-2 bg-white rounded-lg p-4 shadow-sm">
                                <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.photos')}</h2>
                                <p className="text-sm text-gray-600 mb-1">
                                    {showMultiPhotoSelector
                                        ? t('pages.delivery.detail.photoRequirement')
                                        : t('pages.delivery.detail.photoDesc')
                                    }
                                </p>

                                {!showMultiPhotoSelector && <div className="grid grid-cols-3 gap-2 my-4">
                                    <PhotoUploadCard
                                        photoUrl={photos.label?.url}
                                        type="label"
                                        description={`${t('pages.delivery.detail.parcelLabel')}`}
                                        onTakePhoto={takePhoto}
                                        onDelete={deletePhoto}
                                        onView={handleViewPhoto}
                                    />
                                    <PhotoUploadCard
                                        photoUrl={photos.location?.url}
                                        type="location"
                                        description={`${t('pages.delivery.detail.parcelLocation')}`}
                                        onTakePhoto={takePhoto}
                                        onDelete={deletePhoto}
                                        onView={handleViewPhoto}
                                    />
                                    <PhotoUploadCard
                                        photoUrl={photos.houseNumber?.url}
                                        type="houseNumber"
                                        description={`${t('pages.delivery.detail.houseNumber')}`}
                                        onTakePhoto={takePhoto}
                                        onDelete={deletePhoto}
                                        onView={handleViewPhoto}
                                    />
                                </div>}
                                {/* 多图画廊展示 >=1.2.0 */}
                                {showMultiPhotoSelector && <div className="mt-4">
                                    <MultiPhotoGalleryView
                                        photos={photosArray
                                            .filter((photo): photo is PhotoType => photo?.url !== undefined)
                                            .map(photo => photo.url)
                                        }
                                        onDelete={(index: number) => {
                                            deletePhotoArray(index);
                                        }}
                                        maxDisplayCount={6}
                                        onNativeBurstCapture={handleMultiPhotoGalleryBurstCapture}
                                    />
                                </div>}
                                {/* 相册上传 >=1.2.0 */}
                                {showMultiPhotoSelector && photosArray.length < kMaxSelectorPhotoLength && <div className="mt-4">
                                    {multiPhotoSelectorView}
                                </div>}
                            </div>

                            {/* 查看大图 */}
                            <PhotoViewer
                                photoUrl={viewingPhoto}
                                onClose={() => setViewingPhoto(null)}
                            />
                        </div>
                    );
                })()}
            </div>


            {/* 提交按钮 */}
            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <Button
                    block
                    color="primary"
                    loading={isLoading}
                    disabled={showMultiPhotoSelector
                        ? (photosArray.length < kMinSelectorPhotoLength || isLoading)
                        : (!photos.label?.url || !photos.location?.url || !photos.houseNumber?.url || isLoading)
                    }
                    onClick={handleCompleteDelivery}
                >
                    {t('pages.delivery.detail.complete')}
                </Button>
            </div>
        </div>
    );
};

export default DspDeliveredPodPage;
